import { faDog } from '@fortawesome/pro-light-svg-icons/faDog';
import { faLocationDot } from '@fortawesome/pro-light-svg-icons/faLocationDot';
import { faRuler } from '@fortawesome/pro-light-svg-icons/faRuler';
import { faHeartPulse } from '@fortawesome/pro-regular-svg-icons/faHeartPulse';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AppointmentListItem, PetListItem } from '@spike/appointments-list-model';
import clsx from 'clsx';
import PetAvatar from 'components/Bookings2/UI/PetAvatar/PetAvatar';
import StatusButton from 'components/OrderDetail/StatusButton';
import { useKanbanAppointmentCardStyles } from 'components/UI/V2/KanbanBoard/AppointmentCard/KanbanAppointmentCard.styles';
import Checkbox from 'components/UI/V3/Forms/Checkbox';
import { useTimeZone } from 'hooks';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import React from 'react';

interface KanbanAppointmentCardProps {
    appointment: AppointmentListItem;
    selected?: boolean;
    scrollerContainerId?: string;
    className?: string;
    onToggleSelect?: (checked: boolean) => void;
    onShowOrder?: () => void;
    onShowPet?: () => void;
    disabled?: boolean;
}

export const KanbanAppointmentCard: React.FC<KanbanAppointmentCardProps> = props => {
    const classes = useKanbanAppointmentCardStyles();
    const timezone = useTimeZone();
    const { getOrganizationMarketplace } = useOrganizationMarketplaces();
    const date = props.appointment.startAt.tz(timezone);
    const locationName = getOrganizationMarketplace(props.appointment.marketplaceId)?.name;
    const breedName = props.appointment.pet.breed?.name;
    const petWeight = (props.appointment.pet as PetListItem & { weight?: number | null }).weight;
    const weightLabel =
        typeof petWeight === 'number' && Number.isFinite(petWeight) && petWeight > 0
            ? `${Math.trunc(petWeight)} lbs`
            : null;

    return (
        <div
            className={clsx(classes.root, props.className, !props.disabled && classes.grabbable)}
            onClick={props.onShowOrder}
        >
            <div className={classes.header}>
                <div
                    data-no-drag
                    className={clsx(classes.checkbox, props.disabled && classes.checkboxDisabled)}
                    onClick={event => event.stopPropagation()}
                >
                    <Checkbox
                        checked={props.disabled ? false : props.selected}
                        onChange={props.disabled ? undefined : props.onToggleSelect}
                    />
                </div>
                <div className={classes.nameRow}>
                    <button
                        type="button"
                        data-no-drag
                        className={classes.name}
                        onClick={event => {
                            event.stopPropagation();
                            props.onShowPet?.();
                        }}
                    >
                        {props.appointment.pet.name}
                    </button>
                    {props.appointment.pet.hasMedicalConditions && (
                        <FontAwesomeIcon
                            icon={faHeartPulse}
                            className={classes.heart}
                        />
                    )}
                </div>
                <div
                    data-no-drag
                    className={classes.status}
                    onClick={event => event.stopPropagation()}
                >
                    <StatusButton
                        appointmentId={props.appointment.appointmentId}
                        apppointmentMarketplaceId={props.appointment.marketplaceId}
                        appointmentStatus={props.appointment.status}
                        appointmentClosedAt={props.appointment.closedAt ?? undefined}
                        appointmentCancelationReasonId={props.appointment.cancellationReasonId ?? null}
                        recurringUuid={props.appointment.recurringUuid ?? undefined}
                        customerFirstName={props.appointment.customer.firstName}
                        customerLastName={props.appointment.customer.lastName}
                        reloadOrderAfterStatusChange={false}
                        businessAreaId={props.appointment.service.businessArea}
                        scrollerContainerId={props.scrollerContainerId}
                        position="fixed"
                        disabled={props.disabled}
                        className={classes.statusButton}
                    />
                </div>
            </div>
            <div className={classes.body}>
                <PetAvatar
                    petType={props.appointment.pet.petType}
                    imageUrl={props.appointment.pet.imageUrl ?? undefined}
                    className={classes.avatar}
                />
                <div className={classes.details}>
                    {(breedName || weightLabel) && (
                        <div className={classes.meta}>
                            {breedName && (
                                <span className={classes.metaItem}>
                                    <FontAwesomeIcon
                                        icon={faDog}
                                        className={classes.metaIcon}
                                    />
                                    <span className={classes.metaText}>{breedName}</span>
                                </span>
                            )}
                            {weightLabel && (
                                <span className={clsx(classes.metaItem, classes.metaWeight)}>
                                    <FontAwesomeIcon
                                        icon={faRuler}
                                        className={classes.metaIcon}
                                    />
                                    <span>{weightLabel}</span>
                                </span>
                            )}
                        </div>
                    )}
                    <p className={classes.schedule}>
                        {date.format('h:mma')} · {props.appointment.service.name}
                    </p>
                    {locationName && (
                        <p className={classes.location}>
                            <FontAwesomeIcon icon={faLocationDot} />
                            <span>{locationName}</span>
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};
